import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import { useCallback, useState } from 'react';
import { Import, useImportContext } from 'src/components/import';
import { ParsedRecord } from 'src/components/import/ImportParser';
import { fetchPjmLocationsList, usePjmLocationUpdateMutation } from 'src/domain/pjm/queries';
import {
    pjmLocationBulkUpdateCsvConfig,
    PjmLocationBulkUpdateCsvColumns,
    pjmLocationBulkUpdateParser,
} from 'src/domain/pjm/import/pjmLocationBulkUpdateParser';
import { mapCsvBulkUpdateToUpdateLocationDto } from 'src/domain/pjm/import/mapCsvBulkUpdateToUpdateLocationDto';
import { PjmLocationDto } from 'src/domain/pjm/dto/PjmLocationDto';
import { PjmLocationsDownloadTemplateSelectedItemsButton } from './PjmLocationsDownloadTemplateSelectedItemsButton';
import { some } from 'lodash';

interface IPjmLocationsBulkUpdateModalProps {
    selectedItems: PjmLocationDto[];
    onClose: (actionTaken: boolean) => void;
}

const PjmLocationsBulkUpdateModalWrapped = ({ selectedItems, onClose }: IPjmLocationsBulkUpdateModalProps) => {
    let {
        viewState: { view, records },
        setImportProgress,
        importErrors,
        setImportErrors,
        importHasErrors,
    } = useImportContext();

    const [loading, setLoading] = useState<boolean>(false);

    const pjmLocationUpdateMutation = usePjmLocationUpdateMutation();

    if (view === 'preview') {
        /**
         * firstly we validate each imported record, but now we need to check duplicates
         * so for this we need to have all records - that is why this validation is not a part of main parser.
         */
        records = validateLocationDuplicates(records);
    }

    const handleBulkUpdateSubmit = useCallback(async (records: ParsedRecord<PjmLocationBulkUpdateCsvColumns>[]) => {
        if (!records.length) return;

        let imported = 0;
        let failed = 0;
        let total = records.length;
        setImportProgress({ imported, failed, total });

        setLoading(true);

        const locationsMap = await getLocationsMap(records);

        let row = 0;
        for (let record of records) {
            /** do not make API call for records with validation errors, but count them in progress */
            if (recordHasValidationError(record)) {
                failed++;
                setImportProgress({ imported, failed, total });
                row++;
                continue;
            }

            try {
                const locationId = record['Location ID*'].parsedValue;
                const location = locationsMap.get(locationId);
                if (!location) {
                    throw new Error(`Location with ID = ${locationId} not found!`);
                }

                const { uuid, dto } = mapCsvBulkUpdateToUpdateLocationDto(record, location);

                await pjmLocationUpdateMutation.mutateAsync({ uuid, dto });

                imported++;
                setImportProgress({ imported, failed, total });
            } catch (error) {
                failed++;
                setImportProgress({ imported, failed, total });
                importErrors[row] = (error as Error).message || 'Update failed!';
                setImportErrors(importErrors);
            }
            row++;
        }

        setLoading(false);
    }, []);

    const handleCancel = () => {
        const actionTaken = !!records.length;
        onClose(actionTaken);
    };

    const onDownloadTemplateRender = () => {
        if (selectedItems.length > 0) {
            return (
                <p className="ant-upload-hint" key="download-template-section">
                    Download template with selected locations, which can be adjusted and uploaded &nbsp;
                    <PjmLocationsDownloadTemplateSelectedItemsButton locations={selectedItems} /> or an empty example template file &nbsp;
                    <Import.DownloadTemplateButton type="default" templateName="bulk-update-location-template-example" />
                </p>
            );
        }

        return (
            <div className="ant-upload-hint" key="download-template-section">
                <p>
                    Download an empty example template file{' '}
                    <Import.DownloadTemplateButton type="default" templateName="bulk-update-location-template-example" />
                </p>
                <p>
                    Alternatively, you may select locations in the previous step and then download them here as a template, which can be adjusted and
                    uploaded.
                </p>
            </div>
        );
    };

    const modalActions: JSX.Element[] = [
        ...(importHasErrors ? [<Import.DownloadErrorsButton key="bulk-update-download-errors" />] : []),
        <Button key="bulk-update-cancel-button" disabled={loading} onClick={handleCancel}>
            Close
        </Button>,
        <Import.SubmitButton<PjmLocationBulkUpdateCsvColumns> key="bulk-update-submit-button" onClick={handleBulkUpdateSubmit}>
            Update
        </Import.SubmitButton>,
    ];

    return (
        <Modal
            title="Bulk Update PJM Locations"
            width="80%"
            open
            keyboard={false}
            destroyOnHidden
            maskClosable={false}
            onCancel={handleCancel}
            footer={modalActions}
        >
            <div>
                {view === 'initial' && <Import.UploadZone acceptFileType=".csv" />}
                {view === 'initial' && <Import.ImportTemplate onRender={onDownloadTemplateRender} />}
                {view === 'initial' && <Import.FileDescription />}
                {['preview', 'importing', 'completed'].includes(view) && <Import.Preview<PjmLocationBulkUpdateCsvColumns> />}
                {['importing', 'completed'].includes(view) && <Import.Progress />}
                {view === 'completed' && <Import.CompletedMessage />}
            </div>
        </Modal>
    );
};

export const PjmLocationsBulkUpdateModal = (props: IPjmLocationsBulkUpdateModalProps) => {
    return (
        <Import.Provider<PjmLocationBulkUpdateCsvColumns> csvConfig={pjmLocationBulkUpdateCsvConfig} parser={pjmLocationBulkUpdateParser}>
            <PjmLocationsBulkUpdateModalWrapped {...props} />
        </Import.Provider>
    );
};

function validateLocationDuplicates(records: ParsedRecord<PjmLocationBulkUpdateCsvColumns>[]) {
    const seenRecords = new Set<number>();
    records = records.map(rec => {
        const locationId = rec['Location ID*'].parsedValue;
        if (seenRecords.has(locationId)) {
            rec['Location ID*'].error = `It is a duplicate location with ID = ${locationId}`;
            return rec;
        }

        seenRecords.add(locationId);
        return rec;
    });

    return records;
}

function recordHasValidationError(record: ParsedRecord<PjmLocationBulkUpdateCsvColumns>): boolean {
    return some(record, item => {
        if (item.error) {
            console.info(`Import Error: `, item);
        }
        return !!item.error;
    });
}

async function getLocationsMap(records: ParsedRecord<PjmLocationBulkUpdateCsvColumns>[]) {
    const locationIds = records.map(r => r['Location ID*'].parsedValue);
    const locationsResponse = await fetchPjmLocationsList({ filter: { id: locationIds }, pagination: { pageSize: 10000 } });
    const locationsMap = new Map<number, PjmLocationDto>(locationsResponse.data.map(location => [location.id!, location]));

    return locationsMap;
}
